import type { RouteRecordRaw } from "vue-router"
import { markRaw } from "vue"
import { RESERVATION_FEATURE_READINESS } from "@/features/reservation/reservationFeatureReadiness"
import { injectRouteList } from "@/hooks/useRouter"
import LevelTwoRouter from "@/layout/components/NavigationBar/LevelTwoRouter.vue"
import {
  DASHBOARD_ACTION_PERMISSIONS,
  DASHBOARD_REPORT_PERMISSIONS,
  type PermissionRequirement,
} from "@/utils/roleFeatureAccess"

const Layout = () => import("@/layout/index.vue")
const Overview = () => import("@/views/page/dashboard/components/Overview/Overview.vue")
const OperatingOverview = () => import("@/views/page/dashboard/components/OperatingStatement/OperatingOverview.vue")
const SessionAnalysis = () => import("@/views/page/dashboard/components/OperatingStatement/SessionAnalysis.vue")
const OperatingBillingDetails = () =>
  import("@/views/page/dashboard/components/OperatingStatement/OperatingBillingDetails.vue")
const CollectionStatistics = () =>
  import("@/views/page/dashboard/components/OperatingStatement/CollectionStatistics.vue")
const BusinessIncomeProcess = () =>
  import("@/views/page/dashboard/components/OperatingStatement/BusinessIncomeProcess.vue")
const ProductStatement = () => import("@/views/page/dashboard/components/ProductStatement/ProductStatementV2.vue")
const TableStatement = () => import("@/views/page/dashboard/components/TableStatement/TableStatement.vue")
const TableCategoryStatistics = () =>
  import("@/views/page/dashboard/components/TableStatement/TableCategoryStatistics.vue")
const TableBillingRuleStatistics = () =>
  import("@/views/page/dashboard/components/TableStatement/TableBillingRuleStatistics.vue")
const TablePeriodStatistics = () =>
  import("@/views/page/dashboard/components/TableStatement/TablePeriodStatistics.vue")
const TableOpeningDetails = () =>
  import("@/views/page/dashboard/components/TableStatement/TableOpeningDetails.vue")
const HandoverStatement = () => import("@/views/page/dashboard/components/HandoverStatement/HandoverStatement.vue")
const OperationRecord = () => import("@/views/page/dashboard/components/OperationRecord/OperationRecord.vue")
const SensitiveOperationStat = () => import("@/views/page/dashboard/components/SensitiveOperation/SensitiveOperationStat.vue")
const SensitiveOperationDetail = () => import("@/views/page/dashboard/components/SensitiveOperation/SensitiveOperationDetail.vue")
const SensitiveApprovalTickets = () => import("@/views/page/dashboard/components/SensitiveApproval/SensitiveApprovalTickets.vue")
const SensitiveApprovalConfig = () => import("@/views/page/dashboard/components/SensitiveApproval/SensitiveApprovalConfig.vue")
const EmployeeCommission = () => import("@/views/page/dashboard/components/CommissionStatement/EmployeeCommission.vue")
const OverTimeCommission = () => import("@/views/page/dashboard/components/CommissionStatement/OverTimeCommission.vue")
const ProductCommission = () => import("@/views/page/dashboard/components/CommissionStatement/ProductCommission.vue")
const ReservationDetailReport = () => import("@/views/page/dashboard/components/ReservationStatement/ReservationDetailReport.vue")
const ReservationEmployeeCommissionReport = () =>
  import("@/views/page/dashboard/components/ReservationStatement/ReservationEmployeeCommissionReport.vue")
const RechargeCommission = () => import("@/views/page/dashboard/components/CommissionStatement/RechargeCommission.vue")

const toPermissionArray = (permissions: PermissionRequirement) => Array.isArray(permissions) ? [...permissions] : [permissions]
const sensitiveOperationViewPermissions = toPermissionArray(DASHBOARD_REPORT_PERMISSIONS.sensitiveOperations)
const handoverReportPermissions = toPermissionArray(DASHBOARD_REPORT_PERMISSIONS.shiftReports)
const reservationDetailPermissions = toPermissionArray(DASHBOARD_REPORT_PERMISSIONS.reservationDetails)
const reservationCommissionPermissions = toPermissionArray(DASHBOARD_REPORT_PERMISSIONS.commissionReports)

const businessDashboardRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/business-report/business-report",
    redirect: "/dashboard/business-report/business-report/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "营业统计",
      hidden: true,
      permissions: ["dashboard_info_realtime"],
    },
    children: [
      {
        path: "/dashboard/business-report/business-report/index",
        name: "OperatingOverview",
        component: OperatingOverview,
        meta: {
          title: "营业统计",
          hidden: true,
          permissions: ["dashboard_info_realtime"],
        },
      },
    ],
  },
  {
    path: "/dashboard/business-report/session-analysis",
    redirect: "/dashboard/business-report/session-analysis/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "场次分析",
      hidden: true,
      permissions: ["dashboard_info_realtime"],
    },
    children: [
      {
        path: "/dashboard/business-report/session-analysis/index",
        name: "SessionAnalysis",
        component: SessionAnalysis,
        meta: {
          title: "场次分析",
          hidden: true,
          permissions: ["dashboard_info_realtime"],
        },
      },
    ],
  },
  {
    path: "/dashboard/business-report/billing-details",
    redirect: "/dashboard/business-report/billing-details/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "账单明细",
      hidden: true,
      permissions: ["dashboard_business_order"],
    },
    children: [
      {
        path: "/dashboard/business-report/billing-details/index",
        name: "OperatingBillingDetails",
        component: OperatingBillingDetails,
        meta: {
          title: "账单明细",
          hidden: true,
          permissions: ["dashboard_business_order"],
        },
      },
    ],
  },
  {
    path: "/dashboard/business-report/collection-statistics",
    redirect: "/dashboard/business-report/collection-statistics/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "收款统计",
      hidden: true,
      permissions: ["dashboard_business_all"],
    },
    children: [
      {
        path: "/dashboard/business-report/collection-statistics/index",
        name: "CollectionStatistics",
        component: CollectionStatistics,
        meta: {
          title: "收款统计",
          hidden: true,
          permissions: ["dashboard_business_all"],
        },
      },
    ],
  },
  {
    path: "/dashboard/business-report/business-income-process",
    redirect: "/dashboard/business-report/business-income-process/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "收款日报",
      hidden: true,
      permissions: ["dashboard_business_all"],
    },
    children: [
      {
        path: "/dashboard/business-report/business-income-process/index",
        name: "BusinessIncomeProcess",
        component: BusinessIncomeProcess,
        meta: {
          title: "收款日报",
          hidden: true,
          permissions: ["dashboard_business_all"],
        },
      },
    ],
  },
]

const reservationDashboardRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/reservation-report/reservation-details",
    redirect: "/dashboard/reservation-report/reservation-details/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "预订明细",
      hidden: true,
      scopeTypes: ["shop", "chain-store"],
      permissions: reservationDetailPermissions,
    },
    children: [
      {
        path: "/dashboard/reservation-report/reservation-details/index",
        name: "ReservationDetailReport",
        component: ReservationDetailReport,
        meta: {
          title: "预订明细",
          hidden: true,
          scopeTypes: ["shop", "chain-store"],
          permissions: reservationDetailPermissions,
        },
      },
    ],
  },
]

const tableDashboardRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/table-report/table-overview",
    redirect: "/dashboard/table-report/table-overview/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "桌台概览",
      hidden: true,
      permissions: ["dashboard_table"],
    },
    children: [
      {
        path: "/dashboard/table-report/table-overview/index",
        name: "TableStatement",
        component: TableStatement,
        meta: {
          title: "桌台概览",
          hidden: true,
          permissions: ["dashboard_table"],
        },
      },
    ],
  },
  {
    path: "/dashboard/table-report/category-statistics",
    redirect: "/dashboard/table-report/category-statistics/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "分类统计",
      hidden: true,
      permissions: ["dashboard_table"],
    },
    children: [
      {
        path: "/dashboard/table-report/category-statistics/index",
        name: "TableCategoryStatistics",
        component: TableCategoryStatistics,
        meta: {
          title: "分类统计",
          hidden: true,
          permissions: ["dashboard_table"],
        },
      },
    ],
  },
  {
    path: "/dashboard/table-report/billing-rule-statistics",
    redirect: "/dashboard/table-report/billing-rule-statistics/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "计费规则统计",
      hidden: true,
      permissions: ["dashboard_table"],
    },
    children: [
      {
        path: "/dashboard/table-report/billing-rule-statistics/index",
        name: "TableBillingRuleStatistics",
        component: TableBillingRuleStatistics,
        meta: {
          title: "计费规则统计",
          hidden: true,
          permissions: ["dashboard_table"],
        },
      },
    ],
  },
  {
    path: "/dashboard/table-report/period-statistics",
    redirect: "/dashboard/table-report/period-statistics/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "包段规则分析",
      hidden: true,
      permissions: ["dashboard_table"],
    },
    children: [
      {
        path: "/dashboard/table-report/period-statistics/index",
        name: "TablePeriodStatistics",
        component: TablePeriodStatistics,
        meta: {
          title: "包段规则分析",
          hidden: true,
          permissions: ["dashboard_table"],
        },
      },
    ],
  },
  {
    path: "/dashboard/table-report/opening-details",
    redirect: "/dashboard/table-report/opening-details/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "开台明细",
      hidden: true,
      permissions: ["dashboard_table"],
    },
    children: [
      {
        path: "/dashboard/table-report/opening-details/index",
        name: "TableOpeningDetails",
        component: TableOpeningDetails,
        meta: {
          title: "开台明细",
          hidden: true,
          permissions: ["dashboard_table"],
        },
      },
    ],
  },
]

const commissionDashboardRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/commission-report/employee-commission",
    redirect: "/dashboard/commission-report/employee-commission/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "提成概览",
      hidden: true,
      permissions: ["dashboard_worker_commission"],
    },
    children: [
      {
        path: "/dashboard/commission-report/employee-commission/index",
        name: "EmployeeCommission",
        component: EmployeeCommission,
        meta: {
          title: "提成概览",
          hidden: true,
          permissions: ["dashboard_worker_commission"],
        },
      },
    ],
  },
  {
    path: "/dashboard/commission-report/product-commission",
    redirect: "/dashboard/commission-report/product-commission/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "商品提成明细",
      hidden: true,
      permissions: ["dashboard_worker_commission"],
    },
    children: [
      {
        path: "/dashboard/commission-report/product-commission/index",
        name: "ProductCommission",
        component: ProductCommission,
        meta: {
          title: "商品提成明细",
          hidden: true,
          permissions: ["dashboard_worker_commission"],
        },
      },
    ],
  },
  {
    path: "/dashboard/commission-report/overtime-commission",
    redirect: "/dashboard/commission-report/overtime-commission/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "加班/服务提成明细",
      hidden: true,
      permissions: ["dashboard_worker_commission"],
    },
    children: [
      {
        path: "/dashboard/commission-report/overtime-commission/index",
        name: "OverTimeCommission",
        component: OverTimeCommission,
        meta: {
          title: "加班/服务提成明细",
          hidden: true,
          permissions: ["dashboard_worker_commission"],
        },
      },
    ],
  },
  {
    path: "/dashboard/commission-report/recharge-commission",
    redirect: "/dashboard/commission-report/recharge-commission/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "充值提成明细",
      hidden: true,
      permissions: ["dashboard_worker_commission"],
    },
    children: [
      {
        path: "/dashboard/commission-report/recharge-commission/index",
        name: "RechargeCommission",
        component: RechargeCommission,
        meta: {
          title: "充值提成明细",
          hidden: true,
          permissions: ["dashboard_worker_commission"],
        },
      },
    ],
  },
  ...(RESERVATION_FEATURE_READINESS.employeeCommissionApiReady
    ? [
        {
          path: "/dashboard/commission-report/reservation-commission",
          redirect: "/dashboard/commission-report/reservation-commission/index",
          component: markRaw(LevelTwoRouter),
          meta: {
            title: "预订提成明细",
            hidden: true,
            scopeTypes: ["chain-store"],
            permissions: reservationCommissionPermissions,
          },
          children: [
            {
              path: "/dashboard/commission-report/reservation-commission/index",
              name: "ReservationEmployeeCommissionReport",
              component: ReservationEmployeeCommissionReport,
              meta: {
                title: "预订提成明细",
                hidden: true,
                scopeTypes: ["chain-store"],
                permissions: reservationCommissionPermissions,
              },
            },
          ],
        },
      ]
    : []),
]

const sensitiveDashboardLegacyRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/sensitive-report/sensitive-approval",
    redirect: "/setting/operation-center/operation-approval/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "操作审核",
      hidden: true,
      permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveAudit,
    },
    children: [
      {
        path: "/dashboard/sensitive-report/sensitive-approval/index",
        name: "SensitiveApprovalTickets",
        component: SensitiveApprovalTickets,
        meta: {
          title: "操作审核",
          hidden: true,
          permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveAudit,
        },
      },
    ],
  },
  {
    path: "/dashboard/sensitive-report/sensitive-approval-config",
    redirect: "/setting/operation-center/operation-config/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "操作配置",
      hidden: true,
      permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveConfig,
    },
    children: [
      {
        path: "/dashboard/sensitive-report/sensitive-approval-config/index",
        name: "SensitiveApprovalConfig",
        component: SensitiveApprovalConfig,
        meta: {
          title: "操作配置",
          hidden: true,
          permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveConfig,
        },
      },
    ],
  },
]

const sensitiveOperationReportRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/sensitive-report/sensitive-detail",
    redirect: "/dashboard/sensitive-report/sensitive-detail/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "敏感操作明细",
      hidden: true,
      permissions: sensitiveOperationViewPermissions,
    },
    children: [
      {
        path: "/dashboard/sensitive-report/sensitive-detail/index",
        name: "SensitiveOperationDetail",
        component: SensitiveOperationDetail,
        meta: {
          title: "敏感操作明细",
          hidden: true,
          permissions: sensitiveOperationViewPermissions,
        },
      },
    ],
  },
  {
    path: "/dashboard/sensitive-report/sensitive-operation",
    redirect: "/dashboard/sensitive-report/sensitive-operation/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "敏感操作汇总",
      hidden: true,
      permissions: sensitiveOperationViewPermissions,
    },
    children: [
      {
        path: "/dashboard/sensitive-report/sensitive-operation/index",
        name: "SensitiveOperationStat",
        component: SensitiveOperationStat,
        meta: {
          title: "敏感操作汇总",
          hidden: true,
          permissions: sensitiveOperationViewPermissions,
        },
      },
    ],
  },
]

const sensitiveDashboardRoute: RouteRecordRaw[] = [
  ...injectRouteList(sensitiveDashboardLegacyRoute),
  ...injectRouteList(sensitiveOperationReportRoute),
]

const firstLevelDashboardRoute: RouteRecordRaw[] = [
  {
    path: "/dashboard/overview",
    redirect: "/dashboard/overview/index",
    meta: {
      title: "数据概览",
      permissions: ["dashboard_info_all"],
    },
    children: [
      {
        path: "/dashboard/overview/index",
        name: "Dashboard",
        component: Overview,
        meta: {
          title: "数据概览",
          permissions: ["dashboard_info_all"],
        },
      },
    ],
  },
  {
    path: "/dashboard/business-report",
    redirect: "/dashboard/business-report/business-report",
    meta: {
      title: "营业报表",
      permissions: ["dashboard_business_all"],
      activeMenu: '/dashboard/business-report',
    },
    children: injectRouteList(businessDashboardRoute),
  },
  {
    path: "/dashboard/reservation-report",
    redirect: "/dashboard/reservation-report/reservation-details",
    meta: {
      title: "预订报表",
      scopeTypes: ["shop", "chain-store"],
      permissions: reservationDetailPermissions,
      activeMenu: "/dashboard/reservation-report",
    },
    children: injectRouteList(reservationDashboardRoute),
  },
  {
    path: "/dashboard/product-report",
    redirect: "/dashboard/product-report/index",
    meta: {
      title: "商品报表",
      permissions: ["dashboard_product"],
      activeMenu: '/dashboard/product-report',
    },
    children: [
      {
        path: "/dashboard/product-report/index",
        name: "ProductStatement",
        component: ProductStatement,
        meta: {
          title: "商品报表",
          hidden: true,
          permissions: ["dashboard_product"],
        },
      },
    ],
  },
  {
    path: "/dashboard/table-report",
    redirect: "/dashboard/table-report/table-overview",
    meta: {
      title: "桌台报表",
      permissions: ["dashboard_table"],
      activeMenu: '/dashboard/table-report',
    },
    children: injectRouteList(tableDashboardRoute),
  },
  {
    path: "/dashboard/commission-report",
    redirect: "/dashboard/commission-report/employee-commission",
    meta: {
      title: "提成报表",
      permissions: ["dashboard_worker_commission"],
      activeMenu: '/dashboard/commission-report',
    },
    children: injectRouteList(commissionDashboardRoute),
  },
  {
    path: "/dashboard/sensitive-report",
    redirect: "/dashboard/sensitive-report/sensitive-detail",
    meta: {
      title: "敏感操作",
      permissions: sensitiveOperationViewPermissions,
      activeMenu: '/dashboard/sensitive-report',
    },
    children: sensitiveDashboardRoute,
  },
  {
    path: "/dashboard/operation-record",
    redirect: "/dashboard/operation-record/index",
    meta: {
      title: "操作记录",
      permissions: ["dashboard_operation_record"],
      activeMenu: '/dashboard/operation-record',
    },
    children: [
      {
        path: "/dashboard/operation-record/index",
        name: "OperationRecord",
        component: OperationRecord,
        meta: {
          title: "操作记录",
          hidden: true,
          permissions: ["dashboard_operation_record"],
        },
      },
    ],
  },
]

const handoverReport: RouteRecordRaw[] = [
  {
    path: "/dashboard/handover-report",
    component: Layout,
    redirect: "/dashboard/handover-report/index",
    children: [
      {
        path: "/dashboard/handover-report/index",
        name: "HandoverStatement",
        component: HandoverStatement,
        meta: {
          title: "交接班",
          showTag: false,
          svgIcon: "nav-dashboard",
          permissions: handoverReportPermissions,
        },
      },
    ],
    meta: {
      title: "交接班",
      showTag: false,
      order: 2,
      permissions: handoverReportPermissions,
    },
  },
]

const dashboard: RouteRecordRaw[] = [
  {
    path: "/dashboard",
    component: Layout,
    redirect: "/dashboard/overview",
    children: injectRouteList(firstLevelDashboardRoute),
    // children: [
    //   {
    //     path: "index",
    //     component: Dashboard,
    //     name: "Dashboard",
    //     meta: {
    //       title: "报表",
    //       showTag: false,
    //       svgIcon: "nav-dashboard",
    //     }
    //   }
    // ],
    meta: {
      title: "报表",
      showTag: false,
      svgIcon: "nav-dashboard",
      order: 5,
      permissions: ["dashboard"],
    },
  },
  ...handoverReport,
]
export default dashboard
